import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
} from '@mui/material';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapCanvas } from '@/components/maps/MapCanvas';
import { LongPressPin } from '@/components/maps/LongPressPin';
import { WildfireDetailDrawer } from '@/components/maps/WildfireDetailDrawer';
import { getPublicWildfires, getPublicTenants, getPublicSeasons } from '../../api/public';

// Fix Leaflet default marker icons
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const activeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = defaultIcon;

// South Africa centre
const DEFAULT_CENTER = [-29.0, 26.0];
const DEFAULT_ZOOM = 6;

// Sentinel for "no season filter" — keeps the Select value a string.
const SEASON_ALL = '__all__';

const PublicMapPage = () => {
  const [wildfires, setWildfires] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeOnly, setActiveOnly] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('');
  const [season, setSeason] = useState(SEASON_ALL);
  const [seasonOptions, setSeasonOptions] = useState([]);
  // Bottom drawer instead of a Leaflet popup — same as the signed-in map.
  const [drawerFire, setDrawerFire] = useState(null);

  const fetchWildfires = async (active, tenantId, seasonTag) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (active) params.active_only = 'true';
      if (tenantId) params.tenant_id = tenantId;
      if (seasonTag && seasonTag !== SEASON_ALL) params.fire_season = seasonTag;
      const data = await getPublicWildfires(params);
      setWildfires(data);
    } catch (err) {
      console.error('Failed to load wildfires:', err);
      setError('Failed to load wildfire data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const data = await getPublicTenants();
        setTenants(data);
      } catch (err) {
        console.error('Failed to load FPA areas:', err);
      }

      // Seed the season picker and default to the latest season so the map
      // opens on the most recent fires, not the full history. Prefer today's
      // season when it has public fires, else fall back to the newest one
      // that does (the list is sorted newest-first).
      let defaultSeason = SEASON_ALL;
      try {
        const seasonsData = await getPublicSeasons();
        const seasons = seasonsData.seasons || [];
        setSeasonOptions(seasons);
        if (seasons.length) {
          const current = seasonsData.current_season;
          defaultSeason = current && seasons.includes(current) ? current : seasons[0];
        }
        setSeason(defaultSeason);
      } catch (err) {
        // Non-fatal: the picker just stays on "All seasons".
        console.error('Failed to load wildfire seasons:', err);
      }

      fetchWildfires(false, '', defaultSeason);
    };
    init();
  }, []);

  const handleActiveOnlyChange = (e) => {
    const checked = e.target.checked;
    setActiveOnly(checked);
    fetchWildfires(checked, tenantFilter, season);
  };

  const handleTenantChange = (e) => {
    const val = e.target.value;
    setTenantFilter(val);
    fetchWildfires(activeOnly, val, season);
  };

  const handleSeasonChange = (e) => {
    const val = e.target.value;
    setSeason(val);
    fetchWildfires(activeOnly, tenantFilter, val);
  };

  const getMarkerCoords = (wildfire) => {
    try {
      const loc = wildfire.location;
      if (!loc) return null;
      const coords = loc.coordinates || (loc.geojson && loc.geojson.coordinates);
      if (!coords || coords.length < 2) return null;
      // GeoJSON: [longitude, latitude]
      return [coords[1], coords[0]];
    } catch {
      return null;
    }
  };

  const getTenantName = (tenantId) => {
    const t = tenants.find((t) => t.id === tenantId);
    return t ? t.name : `FPA #${tenantId}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Toolbar — tighter padding + wrappable controls on mobile so the map gets more vertical space */}
      <Paper
        elevation={1}
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1, sm: 1.5 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 3 },
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6" component="h1" sx={{ mr: { xs: 0, sm: 2 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Wildfire Map
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={activeOnly}
              onChange={handleActiveOnlyChange}
              color="error"
              size="small"
            />
          }
          label="Active only"
        />

        <FormControl size="small" sx={{ minWidth: { xs: 140, sm: 200 }, flexGrow: { xs: 1, sm: 0 } }}>
          <InputLabel>FPA Area</InputLabel>
          <Select
            value={tenantFilter}
            onChange={handleTenantChange}
            label="FPA Area"
          >
            <MenuItem value=""><em>All Areas</em></MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {seasonOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 160 } }}>
            <InputLabel>Season</InputLabel>
            <Select value={season} onChange={handleSeasonChange} label="Season">
              <MenuItem value={SEASON_ALL}><em>All seasons</em></MenuItem>
              {seasonOptions.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {loading && <CircularProgress size={20} />}

        {!loading && (
          <Chip
            label={`${wildfires.length} fire${wildfires.length !== 1 ? 's' : ''}`}
            size="small"
            color={wildfires.some((w) => w.is_active) ? 'error' : 'default'}
          />
        )}
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Map — public/anonymous, so gear is in 'basic' mode: tile layer +
          pin/polygon toggles only, no parcels/boundaries (those are staff
          endpoints and would 401 silently). */}
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <MapCanvas
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          variant="screen"
          gearMode="basic"
          className="!h-full !min-h-0 !rounded-none !border-0"
        >
          <LongPressPin />
          {wildfires.map((wildfire) => {
            const coords = getMarkerCoords(wildfire);
            if (!coords) return null;
            const icon = wildfire.is_active ? activeIcon : defaultIcon;
            return (
              <Marker
                key={wildfire.id}
                position={coords}
                icon={icon}
                eventHandlers={{ click: () => setDrawerFire(wildfire) }}
              />
            );
          })}
        </MapCanvas>

        {/* Empty state overlay */}
        {!loading && wildfires.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              pointerEvents: 'none',
            }}
          >
            <Paper elevation={3} sx={{ px: { xs: 2, sm: 3 }, py: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No public wildfires to display
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>

      {/* No onViewDetails: there is no public detail page to send anyone to. */}
      <WildfireDetailDrawer
        open={Boolean(drawerFire)}
        onOpenChange={(o) => { if (!o) setDrawerFire(null); }}
        wildfire={drawerFire}
      />
    </Box>
  );
};

export default PublicMapPage;
